import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { getRewiringPrice, rewireModifiers } from '../data/pricing';
import { trackRewiringEvents } from '../utils/tracking';
import { Link } from 'react-router-dom';

export const RewireQuoteCalculator = ({ showFullForm = false }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    propertyType: '',
    bedrooms: '',
    propertyStatus: '',
    rewireType: ''
  });
  const [estimate, setEstimate] = useState(null);
  const [started, setStarted] = useState(false);

  const handleStart = () => {
    setStarted(true);
    trackRewiringEvents.calculatorStarted();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateEstimate = () => {
    const isOccupied = formData.propertyStatus === 'occupied';
    const result = getRewiringPrice(
      parseInt(formData.bedrooms),
      formData.propertyType,
      isOccupied
    );
    
    setEstimate(result);
    
    // Track calculator completion
    trackRewiringEvents.calculatorCompleted({
      propertyType: formData.propertyType,
      bedrooms: formData.bedrooms,
      priceShown: result.available ? result.price : 'individual_quotation'
    });
  };

  const handleNext = () => {
    if (step === 3) {
      calculateEstimate();
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const resetCalculator = () => {
    setStep(1);
    setFormData({ propertyType: '', bedrooms: '', propertyStatus: '', rewireType: '' });
    setEstimate(null);
    setStarted(false);
  };

  if (!started) {
    return (
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Get an Instant Price Estimate</CardTitle>
          <CardDescription className="text-base">
            Find out what your property rewire might cost in under 60 seconds
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleStart} 
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
          >
            Start Price Estimate
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-gray-500 text-center mt-4">
            No personal information required at this stage
          </p>
        </CardContent>
      </Card>
    );
  }

  if (estimate) {
    return (
      <Card className="border-blue-200">
        <CardHeader className="bg-gradient-to-br from-blue-50 to-white text-center">
          <CardTitle className="text-2xl mb-4">Your Estimated Price</CardTitle>
          
          {estimate.available ? (
            <>
              <div className="text-5xl font-bold text-blue-600 mb-2">
                {estimate.price}
              </div>
              <CardDescription className="text-base text-gray-700">
                {estimate.propertyType}
              </CardDescription>
              
              {estimate.occupiedUplift && (
                <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    💼 {estimate.occupiedUplift.label}
                  </p>
                  <p className="text-sm text-gray-700">
                    Additional {estimate.occupiedUplift.price}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    {estimate.occupiedUplift.note}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 text-orange-600 mb-4">
                <AlertCircle className="h-8 w-8" />
                <span className="text-2xl font-bold">Individual Quotation Required</span>
              </div>
              <CardDescription className="text-base text-gray-700">
                {estimate.description}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent className="pt-6">
          {estimate.available && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">What's included:</p>
                  <p className="text-sm text-gray-600">{estimate.note}</p>
                  <p className="text-sm text-gray-600 mt-2">
                    Includes RCBO consumer unit with surge protection, full testing & certification
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700 text-center">
              <strong>This is a starting price estimate.</strong> Final cost depends on property size, 
              specification, number of electrical points, access, and property condition.
            </p>
          </div>
          
          <div className="space-y-3">
            <Link to="/rewire-quote">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6">
                Get My Fixed Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <Button 
              variant="outline" 
              onClick={resetCalculator}
              className="w-full"
            >
              Calculate Another Property
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <div className="flex items-center justify-between mb-4">
          <CardTitle className="text-xl">Quick Price Estimate</CardTitle>
          <span className="text-sm text-gray-500">Step {step} of 3</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Step 1: Property Type */}
        {step === 1 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What type of property?</Label>
            <RadioGroup value={formData.propertyType} onValueChange={(val) => handleChange('propertyType', val)}>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="flat" id="flat" />
                <Label htmlFor="flat" className="flex-1 cursor-pointer">Flat / Apartment</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="terraced" id="terraced" />
                <Label htmlFor="terraced" className="flex-1 cursor-pointer">Terraced House</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="semi-detached" id="semi" />
                <Label htmlFor="semi" className="flex-1 cursor-pointer">Semi-Detached House</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="detached" id="detached" />
                <Label htmlFor="detached" className="flex-1 cursor-pointer">Detached House</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="other" id="other" />
                <Label htmlFor="other" className="flex-1 cursor-pointer">Other</Label>
              </div>
            </RadioGroup>
          </div>
        )}
        
        {/* Step 2: Bedrooms */}
        {step === 2 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">How many bedrooms?</Label>
            <RadioGroup value={formData.bedrooms} onValueChange={(val) => handleChange('bedrooms', val)}>
              <div className="grid grid-cols-2 gap-3">
                {['Studio', '1', '2', '3', '4', '5', '6+'].map((bed) => (
                  <div key={bed} className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value={bed} id={`bed-${bed}`} />
                    <Label htmlFor={`bed-${bed}`} className="flex-1 cursor-pointer">
                      {bed} {bed !== 'Studio' && bed !== '6+' ? 'Bedroom' : ''}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>
        )}
        
        {/* Step 3: Property Status */}
        {step === 3 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">Property status?</Label>
            <RadioGroup value={formData.propertyStatus} onValueChange={(val) => handleChange('propertyStatus', val)}>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="vacant" id="vacant" />
                <Label htmlFor="vacant" className="flex-1 cursor-pointer">Vacant / Empty</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="occupied" id="occupied" />
                <Label htmlFor="occupied" className="flex-1 cursor-pointer">Occupied / Furnished</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="refurbishment" id="refurb" />
                <Label htmlFor="refurb" className="flex-1 cursor-pointer">Being Refurbished</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="purchased" id="purchased" />
                <Label htmlFor="purchased" className="flex-1 cursor-pointer">Recently Purchased</Label>
              </div>
            </RadioGroup>
          </div>
        )}
        
        {/* Navigation */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <Button variant="outline" onClick={handleBack} className="flex-1">
              Back
            </Button>
          )}
          <Button 
            onClick={handleNext}
            disabled={
              (step === 1 && !formData.propertyType) ||
              (step === 2 && !formData.bedrooms) ||
              (step === 3 && !formData.propertyStatus)
            }
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {step === 3 ? 'See My Estimate' : 'Next'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
